// @ts-check
// Biblioteca de imagens dos e-mails (spec/email.md seções 7 e 19.4): enviar (arrastar ou escolher, até 10 de uma vez),
// grade com nome, tamanho e dimensões, busca pelo nome, "Usar" e "Excluir" (em uso: mostra onde; já enviada: some só
// da biblioteca). "Remover do ar" (flag SECURITY, RISCO-15) apaga o arquivo público: os e-mails já enviados ficam sem
// a imagem. Erros de cada arquivo aparecem na linha dele, sem parar os outros.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { deleteEmailAsset, listEmailAssets, purgeEmailAsset, uploadEmailAsset } from '../../data/email-templates.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatNumber } from '../../lib/format.js';
import { formatFileSize } from '../../lib/conversation-format.js';
import { EmailDialog } from './email-dialog.js';
import { emailAssetDisplayUrl, rememberEmailAssetUrls } from './local-preview.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailAsset} EmailAsset
 * @typedef {import('../../data/email-templates.js').EmailAssetUsage} EmailAssetUsage
 * @typedef {{ key: string, name: string, status: 'UPLOADING' | 'DONE' | 'FAILED', error?: string }} UploadRow
 */

const ACCEPTED_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif']);
const MAXIMUM_FILES_AT_ONCE = 10;
const MAXIMUM_BYTES = 2 * 1024 * 1024;

/**
 * Mensagem do erro de um arquivo, pelo código e pelo motivo do servidor.
 * @param {unknown} error
 */
export function imageUploadErrorMessage(error) {
  if (error instanceof ApiError) {
    if (error.code === 'FILE_TOO_LARGE') return t('emailDesign.images.errors.size');
    if (error.code === 'EMAIL_ASSET_LIMIT') return t('emailDesign.images.errors.limit');
    if (error.code === 'EMAIL_IMAGE_INVALID') {
      const reason = String(error.details?.reason ?? '');
      if (reason === 'DIMENSIONS') return t('emailDesign.images.errors.dimensions');
      if (reason === 'SIZE') return t('emailDesign.images.errors.size');
      return t('emailDesign.images.errors.type');
    }
  }
  return getErrorMessage(error);
}

/** @param {File} file */
function localFileProblem(file) {
  if (!ACCEPTED_TYPES.has(file.type)) return t('emailDesign.images.errors.type');
  if (file.size > MAXIMUM_BYTES) return t('emailDesign.images.errors.size');
  return null;
}

/**
 * @param {{ onClose: () => void, onSelect?: (asset: EmailAsset) => void }} props
 */
export function ImageLibraryDialog({ onClose, onSelect }) {
  const [assets, setAssets] = useState(/** @type {EmailAsset[] | null} */ (null));
  const [usage, setUsage] = useState(/** @type {EmailAssetUsage | null} */ (null));
  const [cursor, setCursor] = useState(/** @type {string | null} */ (null));
  const [hasMore, setHasMore] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const [uploads, setUploads] = useState(/** @type {UploadRow[]} */ ([]));
  const [isDragging, setIsDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(/** @type {EmailAsset | null} */ (null));
  const [deleteBlockers, setDeleteBlockers] = useState(/** @type {Array<{ kind: string, name: string }> | null} */ (null));
  const [pendingPurge, setPendingPurge] = useState(/** @type {EmailAsset | null} */ (null));
  const [isWorking, setIsWorking] = useState(false);
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const canPurge = hasCurrentPermissionFlag('SECURITY');

  /** @param {{ append?: boolean }} [options] */
  const load = (options = {}) => {
    setLoadFailed(false);
    listEmailAssets({ search: debouncedSearch || undefined, startingAfter: options.append ? cursor : null })
      .then((result) => {
        rememberEmailAssetUrls(result.assets);
        setAssets((current) => (options.append && current ? [...current, ...result.assets] : result.assets));
        setUsage(result.usage ?? null);
        setCursor(result.pageInfo.endCursor);
        setHasMore(result.pageInfo.hasNextPage);
      })
      .catch(() => setLoadFailed(true));
  };

  useEffect(() => {
    load();
  }, [debouncedSearch]);

  /** @param {FileList | File[]} fileList */
  const uploadFiles = async (fileList) => {
    const files = [...fileList];
    const accepted = files.slice(0, MAXIMUM_FILES_AT_ONCE);
    /** @type {UploadRow[]} */
    const rows = accepted.map((file, index) => {
      const problem = localFileProblem(file);
      return { key: `${Date.now()}-${index}`, name: file.name, status: problem ? 'FAILED' : 'UPLOADING', error: problem ?? undefined };
    });
    if (files.length > MAXIMUM_FILES_AT_ONCE) {
      rows.push({ key: `${Date.now()}-more`, name: t('emailDesign.images.tooManyFiles', { count: files.length - MAXIMUM_FILES_AT_ONCE }), status: 'FAILED', error: t('emailDesign.images.atMostTen') });
    }
    setUploads(rows);
    /** @type {EmailAsset[]} */
    const created = [];
    for (let index = 0; index < accepted.length; index += 1) {
      if (rows[index].status === 'FAILED') continue;
      try {
        const asset = await uploadEmailAsset(accepted[index]);
        created.push(asset);
        rememberEmailAssetUrls([asset]);
        setUploads((current) => current.map((row) => (row.key === rows[index].key ? { ...row, status: 'DONE' } : row)));
      } catch (error) {
        const message = imageUploadErrorMessage(error);
        setUploads((current) => current.map((row) => (row.key === rows[index].key ? { ...row, status: 'FAILED', error: message } : row)));
      }
    }
    if (created.length > 0) load();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsWorking(true);
    try {
      const result = await deleteEmailAsset(pendingDelete.id);
      showSuccessSnackbar(result.hiddenOnly ? t('emailDesign.images.hiddenOnly') : t('emailDesign.images.deleted'));
      setPendingDelete(null);
      load();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EMAIL_ASSET_IN_USE') setDeleteBlockers(Array.isArray(error.details?.usages) ? error.details.usages : []);
      else showErrorSnackbar(error);
      setPendingDelete(null);
    } finally {
      setIsWorking(false);
    }
  };

  const confirmPurge = async () => {
    if (!pendingPurge) return;
    setIsWorking(true);
    try {
      await purgeEmailAsset(pendingPurge.id);
      showSuccessSnackbar(t('emailDesign.images.purged'));
      setPendingPurge(null);
      load();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsWorking(false);
    }
  };

  const usageText = usage
    ? t('emailDesign.images.usage', {
      count: formatNumber(usage.count), maximumCount: formatNumber(usage.maximumCount),
      size: formatFileSize(usage.byteSize), maximumSize: formatFileSize(usage.maximumByteSize),
    })
    : '';
  const usageRatio = usage ? Math.max(usage.count / usage.maximumCount, usage.byteSize / usage.maximumByteSize) : 0;

  return html`<${EmailDialog} title=${t('emailDesign.images.title')} onClose=${onClose} size="large" className="email-image-library" testId="email-image-library">
    <div
      class="email-image-dropzone"
      data-dragging=${isDragging ? 'true' : undefined}
      onDragOver=${(/** @type {DragEvent} */ event) => {
        if (!event.dataTransfer?.types.includes('Files')) return;
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave=${() => setIsDragging(false)}
      onDrop=${(/** @type {DragEvent} */ event) => {
        event.preventDefault();
        setIsDragging(false);
        if (event.dataTransfer?.files?.length) uploadFiles(event.dataTransfer.files);
      }}
    >
      <${Icon} name="IconUpload" size=${20} />
      <span>${t('emailDesign.images.dropHint')}</span>
      <${Button} title=${t('emailDesign.images.upload')} icon="IconPlus" size="small" variant="secondary" onClick=${() => fileInputRef.current?.click()} />
      <input ref=${fileInputRef} type="file" accept="image/png,image/jpeg,image/gif" multiple hidden
        onChange=${(/** @type {Event} */ event) => {
          const input = /** @type {HTMLInputElement} */ (event.currentTarget);
          if (input.files?.length) uploadFiles(input.files);
          input.value = '';
        }} />
    </div>
    ${uploads.length > 0
      ? html`<ul class="email-upload-list" aria-live="polite">
          ${uploads.map((row) => html`<li key=${row.key} data-status=${row.status}>
            <${Icon} name=${row.status === 'FAILED' ? 'IconAlertTriangle' : row.status === 'DONE' ? 'IconCheck' : 'IconUpload'} size=${14} />
            <span class="email-upload-name">${row.name}</span>
            <span class="email-upload-status">${row.status === 'FAILED' ? row.error : row.status === 'DONE' ? t('emailDesign.images.uploaded') : t('emailDesign.images.uploading')}</span>
          </li>`)}
        </ul>`
      : null}
    <div class="email-image-toolbar">
      <label class="email-search-field">
        <${Icon} name="IconSearch" size=${14} />
        <input type="search" value=${search} placeholder=${t('emailDesign.images.search')} aria-label=${t('emailDesign.images.search')}
          onInput=${(/** @type {Event} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      ${usage
        ? html`<div class="email-usage-meter" title=${usageText}>
            <span class="email-usage-text">${usageText}</span>
            <span class="email-usage-bar"><span style=${{ width: `${Math.min(100, Math.round(usageRatio * 100))}%` }} data-high=${usageRatio > 0.9 ? 'true' : undefined}></span></span>
          </div>`
        : null}
    </div>
    ${loadFailed
      ? html`<div class="email-inline-error" role="alert">
          <span>${t('emailTemplates.loadError')}</span>
          <${Button} title=${t('emailTemplates.retry')} size="small" variant="secondary" onClick=${() => load()} />
        </div>`
      : assets === null
        ? html`<div class="email-image-grid">${Array.from({ length: 8 }, (_, index) => html`<${Skeleton} key=${index} height=${150} radius=${8} />`)}</div>`
        : assets.length === 0
          ? html`<${EmptyState} icon="IconPhoto" title=${debouncedSearch ? t('emailDesign.images.noResults', { search: debouncedSearch }) : t('emailDesign.images.empty')}
              subtitle=${debouncedSearch ? undefined : t('emailDesign.images.emptyHint')} />`
          : html`<ul class="email-image-grid">
              ${assets.map((asset) => html`<li key=${asset.id} class="email-image-card" data-purged=${asset.purgedAt ? 'true' : undefined}>
                <div class="email-image-thumbnail">
                  ${asset.purgedAt
                    ? html`<span class="email-image-purged"><${Icon} name="IconEyeOff" size=${18} />${t('emailDesign.images.purgedLabel')}</span>`
                    : html`<img src=${emailAssetDisplayUrl(asset)} alt="" loading="lazy" />`}
                </div>
                <div class="email-image-meta">
                  <span class="email-image-name" title=${asset.fileName}>${asset.fileName}</span>
                  <span class="email-image-details">${[formatFileSize(asset.byteSize), asset.width && asset.height ? `${asset.width} x ${asset.height}` : ''].filter(Boolean).join(' | ')}</span>
                </div>
                <div class="email-image-actions">
                  ${onSelect && !asset.purgedAt ? html`<${Button} title=${t('emailDesign.images.use')} size="small" accent="blue" onClick=${() => onSelect(asset)} />` : null}
                  <${Button} title=${t('emailDesign.images.delete')} size="small" variant="secondary" onClick=${() => setPendingDelete(asset)} />
                  ${canPurge && asset.isSent && !asset.purgedAt
                    ? html`<${Button} title=${t('emailDesign.images.purge')} size="small" variant="tertiary" accent="danger" onClick=${() => setPendingPurge(asset)} />`
                    : null}
                </div>
              </li>`)}
            </ul>`}
    ${hasMore ? html`<div class="email-load-more"><${Button} title=${t('emailDesign.images.loadMore')} variant="secondary" size="small" onClick=${() => load({ append: true })} /></div>` : null}
    ${pendingDelete
      ? html`<${Modal} title=${t('emailDesign.images.confirmDeleteTitle')} subtitle=${pendingDelete.isSent ? t('emailDesign.images.confirmDeleteSent') : t('emailDesign.images.confirmDeleteDescription')}
          confirmLabel=${t('emailDesign.images.delete')} confirmAccent="danger" isLoading=${isWorking} onClose=${() => setPendingDelete(null)} onConfirm=${confirmDelete} />`
      : null}
    ${deleteBlockers
      ? html`<${Modal} title=${t('emailDesign.images.inUseTitle')} subtitle=${t('emailDesign.images.inUse')} confirmLabel=${t('common.close')} hideCancel=${true}
          onClose=${() => setDeleteBlockers(null)} onConfirm=${() => setDeleteBlockers(null)}>
          <ul class="email-usage-list">${deleteBlockers.map((usageItem) => html`<li>${usageItem.name || t('emailDesign.images.hiddenUsage')}</li>`)}</ul>
        <//>`
      : null}
    ${pendingPurge
      ? html`<${Modal} title=${t('emailDesign.images.purgeTitle')} subtitle=${t('emailDesign.images.purgeDescription')}
          confirmLabel=${t('emailDesign.images.purge')} confirmAccent="danger" isLoading=${isWorking} onClose=${() => setPendingPurge(null)} onConfirm=${confirmPurge} />`
      : null}
  <//>`;
}
